import React, { FunctionComponent, ChangeEvent, useState, KeyboardEvent, SyntheticEvent, useEffect, useMemo, useCallback } from 'react';
import { Menu, Button, Radio, Classes, MenuItem, MenuDivider, TagInput, Icon } from '@blueprintjs/core';


import './ListFiltersMenus.scss';
import { IDatabaseFilter, ITag, IUser } from '../../../models';
import CustomRadioButton from './CustomRadioButton';
import { IState } from 'shared/store/reducers';
import { useSelector } from 'react-redux';
import { FILTER_TYPE, NO_VALUE_OPERATORS } from './types';


type ApplyCallback = (condition: { type: string; value?: any; }) => void;


type GenericListFilterMenuComponentProps = {
  menuItems: IDatabaseFilter[];
  type: string;
  value: string | undefined;
  onApply: ApplyCallback,
  filter: any;
};

type ListFilterMenuComponentProps = {
  value: string | undefined;
  onApply: ApplyCallback,
  options: IDatabaseFilter[],
  filter: any;
};


export const GenericListFilterMenu: FunctionComponent<GenericListFilterMenuComponentProps> = ({
  menuItems, type, value, onApply, filter
}) => {
  const [filterValue, setFilterValue] = useState<string | undefined>(menuItems[0].operator);
  const [filledValue, setFilledValue] = useState<any>(value);
  const allTags = useSelector((state: IState) => state.tags.tags);

  const handleFilledValue = useCallback((value: any) => {
    setFilledValue(value)
  }, [])

  const handleFilterValue = useCallback((value: string) => {
    setFilterValue(value)
  }, [])

  useEffect(() => {
    if (filter.opr) {
      handleFilterValue(filter.opr)
    }
    if (filter.value) {
      if (type === 'tag') {
        let tagIds: any[] = []
        if (Array.isArray(filter.value)) {
          tagIds = filter.value
        } else {
          tagIds = filter.value?.split(',')
        }
        const tags = allTags.filter(t => tagIds.includes(t.uid))
        handleFilledValue(tags)
      } else {
        handleFilledValue(filter.value)
      }
    }
  }, [filter.value, filter.opr])


  const handleApply = () => {
    let filteredValueAdjusted = filledValue;
    if (!filterValue) {
      return;
    }

    const filter = menuItems.find(m => m.operator === filterValue);
    if (!filter) {
      return;
    }

    if (filter.fillValue && !filledValue && !NO_VALUE_OPERATORS.includes(filter.operator)) {
      return;
    }


    if (Array.isArray(filledValue)) {
      filteredValueAdjusted = filledValue.map((item: any) => {
        return item.uid;
      });
    }
    if (NO_VALUE_OPERATORS.includes(filter.operator)) {
      filteredValueAdjusted = 'empty'
    }

    onApply && onApply({
      type: filterValue,
      value: filteredValueAdjusted,
    });
  };

  const isApplyDisabled = useMemo(() => {
    if (NO_VALUE_OPERATORS.includes(filterValue || '')) {
      return false
    }
    if (type === FILTER_TYPE.TAG) {
      return filledValue?.length === 0
    }
    return !filledValue
  }, [filledValue, filter, filterValue])


  return (
    <Menu className="filters__menu" data-cy='selectedFilterOptions'>
      {
        menuItems.map((menuItem, index) => (
          <CustomRadioButton
            key={index}
            label={menuItem.name}
            value={menuItem.operator}
            groupValue={filterValue}
            type={type}
            fillValue={menuItem.fillValue}
            defaultFilledValue={filledValue}
            onChange={handleFilledValue}
            onCheck={() => handleFilterValue(menuItem.operator)}
            onEnter={handleApply}
            name={filter.name}
          />
        ))
      }
      <Button
        icon="tick"
        className="filters__menu__apply"
        onClick={handleApply}
        disabled={isApplyDisabled}
      >Apply</Button>
    </Menu>
  );
};

export const ListFilterMenuForString: FunctionComponent<ListFilterMenuComponentProps> = ({
  value, onApply, options = [], filter
}) => {
  return (
    <GenericListFilterMenu
      menuItems={options}
      type="string"
      value={value}
      onApply={onApply}
      filter={filter}
    ></GenericListFilterMenu>
  );
};

export const ListFilterMenuForNumber: FunctionComponent<ListFilterMenuComponentProps> = ({
  value, onApply, options, filter
}) => {
  return (
    <GenericListFilterMenu
      menuItems={options}
      type="number"
      value={value}
      onApply={onApply}
      filter={filter}
    ></GenericListFilterMenu>
  );
};

export const ListFilterMenuForDate: FunctionComponent<ListFilterMenuComponentProps> = ({
  value, onApply, options = [], filter
}) => {
  return (
    <GenericListFilterMenu
      menuItems={options}
      type="date"
      value={value}
      onApply={onApply}
      filter={filter}
    ></GenericListFilterMenu>
  );
};

export const ListFilterMenuForSelect: FunctionComponent<ListFilterMenuComponentProps> = ({
  value, onApply, options = [], filter
}) => {
  return (
    <GenericListFilterMenu
      menuItems={options}
      type="select"
      value={value}
      onApply={onApply}
      filter={filter}
    ></GenericListFilterMenu>
  );
};

export const ListFilterMenuForTag: FunctionComponent<ListFilterMenuComponentProps> = ({
  value, onApply, options = [], filter
}) => {
  return (
    <GenericListFilterMenu
      menuItems={options}
      type="tag"
      value={value}
      onApply={onApply}
      filter={filter}
    ></GenericListFilterMenu>
  );
};
